import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Loader2, Send, Eye, Rocket, Ban } from "lucide-react";

export default function WeeklyEmails({ accounts = [], fixedAccountId = null }) {
  const linkable = useMemo(() => accounts.filter((a) => a.shipstation_store_id || a.shipstation_customer_id), [accounts]);
  const [accountId, setAccountId] = useState(fixedAccountId || "");
  useEffect(() => { if (fixedAccountId) setAccountId(fixedAccountId); }, [fixedAccountId]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [running, setRunning] = useState(false);
  const [optOut, setOptOut] = useState(false);

  const load = async (id) => {
    if (!id) { setPreview(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/accounts/${id}/weekly-email/preview`);
      setPreview(data);
      setOptOut(!!data.opt_out);
    } catch (e) { toast.error(formatApiError(e)); setPreview(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(accountId); /* eslint-disable-next-line */ }, [accountId]);

  const toggleOptOut = async () => {
    const next = !optOut;
    try {
      await api.put(`/admin/accounts/${accountId}/weekly-email?opt_out=${next}`);
      setOptOut(next);
      toast.success(next ? "Account opted out of weekly emails" : "Account will receive weekly emails");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/accounts/${accountId}/weekly-email/send`);
      const failedCount = data.failed?.length || 0;
      if (data.sent > 0) {
        toast.success(`Sent to ${data.sent} recipient(s)${failedCount ? ` · ${failedCount} failed` : ""}`);
      } else {
        toast.error(data.failed?.[0]?.error ? `Send failed: ${data.failed[0].error}` : "Send failed for all recipients");
      }
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  const runAll = async () => {
    if (!window.confirm("Send the weekly update now to every opted-in store?")) return;
    setRunning(true);
    try {
      const { data } = await api.post(`/admin/weekly-email/run`);
      toast.success(`Done — sent ${data.sent}, skipped ${data.skipped}, failed ${data.failed}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setRunning(false); }
  };

  return (
    <div data-testid="weekly-emails" className="space-y-4">
      {!fixedAccountId && (
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
        <Mail className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
        <p className="text-sm text-blue-900 leading-relaxed">
          Automated weekly business-update emails go out <strong>every Wednesday at 8:00 AM ET</strong> to each store&apos;s team.
          Preview, send a one-off, toggle opt-out per store, or trigger the whole run manually.
        </p>
      </div>
      )}

      {!fixedAccountId && (
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Store / Account</label>
          <select
            data-testid="weekly-email-account-select"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="w-full md:w-96 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
          >
            <option value="">Select a store…</option>
            {linkable.map((a) => (
              <option key={a.id} value={a.id}>{a.company_name} ({a.boxhero_account_id || a.id.slice(-6)})</option>
            ))}
          </select>
        </div>
        <button data-testid="run-all-weekly-btn" onClick={runAll} disabled={running}
          className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white font-semibold px-4 py-2.5 rounded-md">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Send all now
        </button>
      </div>
      )}

      {accountId && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 py-10 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Building preview…</div>
          ) : preview ? (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <button data-testid="send-weekly-now-btn" onClick={sendNow} disabled={sending || optOut}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-md">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to this store now
                </button>
                <button data-testid="toggle-optout-btn" onClick={toggleOptOut}
                  className={`inline-flex items-center gap-2 font-semibold px-4 py-2 rounded-md border ${optOut ? "bg-red-50 border-red-200 text-red-700" : "bg-white border-slate-300 text-slate-700"}`}>
                  <Ban className="w-4 h-4" /> {optOut ? "Opted out (click to re-enable)" : "Opt this store out"}
                </button>
                <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {preview.recipients.length} recipient(s)</span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Orders (MTD)" value={preview.stats.orders_mtd} />
                <Kpi label="Units (MTD)" value={preview.stats.units_mtd} />
                <Kpi label="This week" value={preview.stats.orders_this_week} />
                <Kpi label="WoW" value={preview.stats.orders_wow_pct == null ? "—" : `${preview.stats.orders_wow_pct}%`} />
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-2">Email preview</p>
                <iframe
                  data-testid="weekly-email-preview-frame"
                  title="Weekly email preview"
                  srcDoc={preview.html}
                  sandbox=""
                  className="w-full h-[560px] border border-slate-200 rounded-lg bg-[#F0F2F1]"
                />
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-400 py-6 text-center">Could not build a preview for this store.</p>
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-xl font-bold text-slate-900">{value}</p>
    </div>
  );
}
